import { type FunctionComponent, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { type ProductVariantFieldsFragment } from '../../__generated__/graphql-client-types';
import { SHOWROOM_LINK } from '../../constants/links';
import { useAddProductToProjects } from '../../hooks/save-to-project/add-product-to-projects.hooks';
import { type PdpProductFamily } from '../../types/product.types';
import { type ProjectInputWithName } from '../../types/project.types';
import { StyledButton } from '../buttons';
import { Link } from '../common-components/link/link.component';
import { MediaGallery } from '../media-gallery/media-gallery.component';
import { BrandLogo } from '../product-components/brand-logo/brand-logo.component';
import { CompareButton } from '../product-components/compare-button/compare-button.component';
import { ProductSaleBanner } from '../product-components/product-sale-banner/product-sale-banner.component';
import { ProductStock } from '../product-components/product-stock/product-stock.component';
import { ProductSummary } from '../product-components/product-summary/product-summary.component';
import { SaleRestrictions } from '../product-components/sale-restrictions/sale-restrictions.component';
import { SaveToProjectButton } from '../product-components/save-to-project-button/save-to-project-button.component';
import { ShippingEligibility } from '../product-components/shipping-eligibility/shipping-eligibility.component';
import { ProductRebate } from '../rebates/product-rebate/product-rebate.component';
import { ShowroomOnlyBadge } from './showroom-only-badge/showroom-only-badge.component';

export type ShowroomProductInformationProps = {
	productUid: number;
	product: PdpProductFamily;
	currentVariant: ProductVariantFieldsFragment;
	currentVariantCountAvailable: number;
	onFindShowroomIsInView?: (boolean) => void;
};

export const ShowroomProductInformation: FunctionComponent<ShowroomProductInformationProps> = ({
	productUid,
	product,
	currentVariant,
	currentVariantCountAvailable,
	onFindShowroomIsInView
}) => {
	const { ref: findShowroomRef, inView: findShowroomInView } = useInView();
	const { saveToProject } = useAddProductToProjects();

	// Will fire the passed down function when Find a Shoowroom's view status changes
	// Allows us to render another Find a Showroom button in the navbar after the main is out of view
	useEffect(() => {
		if (onFindShowroomIsInView) {
			onFindShowroomIsInView(findShowroomInView);
		}
	}, [findShowroomInView]);

	const onSaveToProject = async (projectInput: ProjectInputWithName[]) => {
		await saveToProject(projectInput, currentVariant.id);
	};

	return (
		<div className={'cf flex db-ns flex-column'}>
			<div className={`fl w-100 w-50-ns order-1 pt1-ns pr3-ns`}>
				{/* Product Image */}
				<div className={'v-top mt2'}>
					<BrandLogo brandName={product.brandInfo.name} imageId={product.brandInfo.logo.id} url={product.url} />
					<div className="pa2 center relative">
						<MediaGallery
							title={`${product.brandInfo.name} ${product.productId} ${product.collection?.name} ${product.type}`}
							items={[
								{ ...currentVariant.image, description: `Finish: ${currentVariant.image.description}` },
								...product.media
							]}
							variantImageId={currentVariant.image.id}
							currentVariantImage={currentVariant.image}
						/>
					</div>
				</div>
			</div>
			<div className="fl w-50-ns order-0">
				<div className="db dn-ns monospace f7 mt2">Item # bci{productUid}</div>
				<ShowroomOnlyBadge />
				<ProductSummary
					brand={product.brandInfo.name}
					modelNumber={currentVariant.modelNumber}
					title={product.title}
					collectionName={product.collection ? product.collection.name : ''}
					shouldClampTitle={false}
					reviewSummary={product.reviewSummary}
					productUid={currentVariant.id}
				/>
			</div>
			<div className="fr w-100 w-50-ns order-2">
				<div className={'mt4 mt1-ns'}>
					<div className={'flex flex-column flex-wrap w-100 db-ns'}>
						<div className="db-ns dn">
							<ProductSaleBanner productSales={currentVariant.sales} />
						</div>
						<h3 className="mt3 mb0">Visit Showroom for Price</h3>
						{currentVariant?.saleRestrictions ? (
							<SaleRestrictions
								{...currentVariant.saleRestrictions}
								currentFamilyId={product.id}
								className={`mt3`}
								selectedVariant={currentVariant}
							/>
						) : null}
						<div>{currentVariant && <ProductRebate variantIds={[currentVariant.id]} />}</div>
						<div className={'mt0 db dn-ns'}>
							<ShippingEligibility showZipCodeChanger={true} showOrderedBy={true} isForShowroom={true} />
						</div>
						{product.variants.length > 1 ? (
							<>
								<div className={'dn db-ns f5-ns lh-copy pt3-ns'}>
									{currentVariant.title !== 'N/A' && (
										<>
											<strong>Finish: </strong>
											<span data-automation="finish-name">{currentVariant.title}</span>
											<span className="theme-emphasis"> -</span>
										</>
									)}
									<ProductStock
										count={currentVariantCountAvailable}
										isMadeToOrder={currentVariant.isMadeToOrder}
										className={'dib-ns'}
										brand={product.brandInfo.name}
									/>
								</div>
								<div className={'db dn-ns'}>
									<ProductSaleBanner productSales={currentVariant.sales} />
									<div className={'f5 mt3 pt3 bt b--theme-grey-light'}>
										{currentVariant.title !== 'N/A' && (
											<>
												<strong>Finish: </strong>
												<span data-automation="finish-name">{currentVariant.title}</span>
												<span className="theme-emphasis"> -</span>
											</>
										)}
										<ProductStock
											count={currentVariantCountAvailable}
											isMadeToOrder={currentVariant.isMadeToOrder}
											className={'dib'}
											brand={product.brandInfo.name}
										/>
									</div>
								</div>
							</>
						) : (
							<div className={'mt3 pt3 pa0-ns mt1-ns f5 lh-copy-ns bt b--theme-grey-light bn-ns'}>
								{currentVariant.title !== 'N/A' && (
									<>
										<strong>Finish: </strong>
										<span data-automation="finish-name">{currentVariant.title}</span>
										<span className="theme-emphasis"> -</span>
									</>
								)}
								<ProductStock
									count={currentVariantCountAvailable}
									isMadeToOrder={currentVariant.isMadeToOrder}
									className={'dib'}
									brand={product.brandInfo.name}
								/>
							</div>
						)}

						<div className={'mt4-ns dn db-ns'}>
							<ShippingEligibility showZipCodeChanger={true} showOrderedBy={true} isForShowroom={true} />
						</div>

						<div className="mt3" ref={findShowroomRef}>
							<Link url={SHOWROOM_LINK}>
								<StyledButton buttonStyle="PRIMARY">Find a Showroom</StyledButton>
							</Link>
						</div>
						{/* Extra Controls Widget */}
						<div className="flex flex-column items-start justify-between mt3">
							<div className="flex flex-wrap w-100-l items-end">
								<div className="relative">
									<SaveToProjectButton size="SMALL" onSaveToProject={onSaveToProject} />
								</div>
								<div className="ml2">
									<CompareButton variantId={currentVariant.id} hasBorder={true} size={'SMALL'} automationHook="compare" />
								</div>
							</div>
							<p className="mt3 f6 lh-copy">
								<strong className="db">Ferguson Home is a Ferguson Company.</strong>
								Purchase this product at a Ferguson Showroom near you.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
